import { Application, Container, Ticker } from "pixi.js";
import { FPSMeter } from "./FPSMeter";

export interface GameConfig {
  width: number;
  height: number;
  backgroundColor?: number;
  antialias?: boolean;
  resolution?: number;
  autoDensity?: boolean;
}

export class GameApp {
  public readonly app: Application;
  public get ticker(): Ticker {
    return this.app.ticker;
  }
  private initialized = false;
  private container: HTMLElement;
  private designWidth: number;
  private designHeight: number;
  private resizeCallback: (() => void) | null = null;
  private fullscreenChangeHandler: (() => void) | null = null;
  private fillMode: boolean = false;

  constructor(container: HTMLElement, config: GameConfig) {
    this.container = container;
    this.designWidth = config.width;
    this.designHeight = config.height;
    this.app = new Application();

    this.init(container, config).catch((error) => {
      console.error("Failed to initialize Pixi.js application:", error);
    });
  }

  private async init(container: HTMLElement, config: GameConfig): Promise<void> {
    await this.app.init({
      width: config.width,
      height: config.height,
      backgroundColor: config.backgroundColor ?? 0x000000,
      antialias: config.antialias ?? true,
      resolution: config.resolution ?? (window.devicePixelRatio || 1),
      autoDensity: config.autoDensity ?? true,
    });

    // Expose PixiJS app globally for debugging and dev tools
    (globalThis as any).__PIXI_APP__ = this.app;
    (globalThis as any).__PIXI_STAGE__ = this.app.stage;
    (globalThis as any).__PIXI_RENDERER__ = this.app.renderer;

    container.appendChild(this.app.canvas);
    this.initialized = true;
  }

  async waitForInit(): Promise<void> {
    while (!this.initialized) {
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
  }

  get stage(): Container {
    return this.app.stage;
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  resize(width: number, height: number): void {
    this.app.renderer.resize(width, height);
  }

  /**
   * Smart resize that handles device pixel ratio and maintains aspect ratio
   * Uses design resolution for game logic, scales for display
   * Works with autoDensity: true to automatically handle DPR
   */
  resizeToContainer(container?: HTMLElement, designWidth?: number, designHeight?: number): void {
    const targetContainer = container || this.container;
    const targetDesignWidth = designWidth || this.designWidth;
    const targetDesignHeight = designHeight || this.designHeight;
    
    const rect = targetContainer.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    
    if (containerWidth <= 0 || containerHeight <= 0) {
      return; // Container not visible yet
    }

    // Calculate scale to fit design resolution in container (maintain aspect ratio)
    const scaleX = containerWidth / targetDesignWidth;
    const scaleY = containerHeight / targetDesignHeight;
    const scale = Math.min(scaleX, scaleY);
    
    // Calculate actual display size (maintaining aspect ratio)
    const displayWidth = targetDesignWidth * scale;
    const displayHeight = targetDesignHeight * scale;
    
    // Set canvas CSS size (display size in CSS pixels)
    // This controls how big the canvas appears on screen
    this.app.canvas.style.width = `${displayWidth}px`;
    this.app.canvas.style.height = `${displayHeight}px`;
    
    // Resize renderer to display size
    // With autoDensity: true, PixiJS automatically handles devicePixelRatio
    // The renderer will internally use displayWidth * DPR for actual rendering
    this.app.renderer.resize(displayWidth, displayHeight);
    
    // Scale stage to match design resolution (game logic stays at design resolution)
    // This ensures all game coordinates work at design resolution
    this.app.stage.scale.set(scale);
    
    // Center stage in container
    this.app.stage.x = (containerWidth - displayWidth) / 2;
    this.app.stage.y = (containerHeight - displayHeight) / 2;
  }

  /**
   * Resize to fill entire container - canvas fills space, but stage maintains aspect ratio
   * Canvas fills all available space, but PixiJS stage scales proportionally
   */
  resizeToFillContainer(container?: HTMLElement, designWidth?: number, designHeight?: number): void {
    const targetContainer = container || this.container;
    const targetDesignWidth = designWidth || this.designWidth;
    const targetDesignHeight = designHeight || this.designHeight;
    
    const rect = targetContainer.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    
    if (containerWidth <= 0 || containerHeight <= 0) {
      return; // Container not visible yet
    }

    // Canvas fills entire container
    const displayWidth = containerWidth;
    const displayHeight = containerHeight;
    
    // Set canvas CSS size to fill container
    this.app.canvas.style.width = `${displayWidth}px`;
    this.app.canvas.style.height = `${displayHeight}px`;
    
    // Resize renderer to fill size
    // With autoDensity: true, PixiJS automatically handles devicePixelRatio
    this.app.renderer.resize(displayWidth, displayHeight);
    
    // Calculate scale to fit design resolution in container (maintain aspect ratio)
    const scaleX = displayWidth / targetDesignWidth;
    const scaleY = displayHeight / targetDesignHeight;
    const scale = Math.min(scaleX, scaleY); // Use min to maintain aspect ratio
    
    // Scale stage proportionally (maintains aspect ratio)
    this.app.stage.scale.set(scale);
    
    // Calculate actual game display size (maintaining aspect ratio)
    const gameDisplayWidth = targetDesignWidth * scale;
    const gameDisplayHeight = targetDesignHeight * scale;
    
    // Center stage in canvas (letterboxing/pillarboxing)
    this.app.stage.x = (displayWidth - gameDisplayWidth) / 2;
    this.app.stage.y = (displayHeight - gameDisplayHeight) / 2;
  }

  /**
   * Setup automatic resizing with ResizeObserver and window resize events
   * Returns cleanup function
   * @param fillMode If true, canvas fills entire container without maintaining aspect ratio
   */
  setupAutoResize(container?: HTMLElement, designWidth?: number, designHeight?: number, fillMode: boolean = false): () => void {
    const targetContainer = container || this.container;
    const targetDesignWidth = designWidth || this.designWidth;
    const targetDesignHeight = designHeight || this.designHeight;
    
    // Store fill mode for use in resize handlers
    this.fillMode = fillMode;
    
    let rafId: number | null = null;
    let isResizing = false;

    const updateSize = () => {
      if (isResizing) return;
      
      isResizing = true;
      if (this.fillMode) {
        this.resizeToFillContainer(targetContainer, targetDesignWidth, targetDesignHeight);
      } else {
        this.resizeToContainer(targetContainer, targetDesignWidth, targetDesignHeight);
      }
      isResizing = false;
    };

    // Use ResizeObserver with requestAnimationFrame for smooth updates
    const resizeObserver = new ResizeObserver(() => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        updateSize();
        rafId = null;
      });
    });

    // Handle window resize (for orientation changes, etc.)
    const handleWindowResize = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        updateSize();
        rafId = null;
      });
    };

    // Handle fullscreen changes
    this.fullscreenChangeHandler = () => {
      // Small delay to ensure fullscreen transition completes
      setTimeout(() => {
        updateSize();
      }, 100);
    };

    // Initial size
    updateSize();

    // Observe container size changes
    resizeObserver.observe(targetContainer);
    
    // Handle window resize and orientation change
    window.addEventListener("resize", handleWindowResize);
    window.addEventListener("orientationchange", () => {
      setTimeout(handleWindowResize, 100);
    });
    
    // Handle fullscreen changes
    document.addEventListener("fullscreenchange", this.fullscreenChangeHandler);
    document.addEventListener("webkitfullscreenchange", this.fullscreenChangeHandler);
    document.addEventListener("mozfullscreenchange", this.fullscreenChangeHandler);
    document.addEventListener("MSFullscreenChange", this.fullscreenChangeHandler);

    // Store resize callback for cleanup
    this.resizeCallback = () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
      window.removeEventListener("orientationchange", handleWindowResize);
      if (this.fullscreenChangeHandler) {
        document.removeEventListener("fullscreenchange", this.fullscreenChangeHandler);
        document.removeEventListener("webkitfullscreenchange", this.fullscreenChangeHandler);
        document.removeEventListener("mozfullscreenchange", this.fullscreenChangeHandler);
        document.removeEventListener("MSFullscreenChange", this.fullscreenChangeHandler);
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };

    return this.resizeCallback;
  }

  /**
   * Check if currently in fullscreen mode
   */
  get isFullscreen(): boolean {
    return !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
  }

  /**
   * Enter fullscreen mode
   */
  async enterFullscreen(element?: HTMLElement): Promise<void> {
    const targetElement = element || this.container;
    
    try {
      if (targetElement.requestFullscreen) {
        await targetElement.requestFullscreen();
      } else if ((targetElement as any).webkitRequestFullscreen) {
        await (targetElement as any).webkitRequestFullscreen();
      } else if ((targetElement as any).mozRequestFullScreen) {
        await (targetElement as any).mozRequestFullScreen();
      } else if ((targetElement as any).msRequestFullscreen) {
        await (targetElement as any).msRequestFullscreen();
      } else {
        throw new Error("Fullscreen API not supported");
      }
      
      // Resize after entering fullscreen
      setTimeout(() => {
        this.resizeToContainer();
      }, 100);
    } catch (error) {
      console.error("Failed to enter fullscreen:", error);
      throw error;
    }
  }

  /**
   * Exit fullscreen mode
   */
  async exitFullscreen(): Promise<void> {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      } else {
        throw new Error("Fullscreen API not supported");
      }
      
      // Resize after exiting fullscreen
      setTimeout(() => {
        this.resizeToContainer();
      }, 100);
    } catch (error) {
      console.error("Failed to exit fullscreen:", error);
      throw error;
    }
  }

  /**
   * Toggle fullscreen mode
   */
  async toggleFullscreen(element?: HTMLElement): Promise<void> {
    if (this.isFullscreen) {
      await this.exitFullscreen();
    } else {
      await this.enterFullscreen(element);
    }
  }

  destroy(): void {
    // Cleanup resize observers
    if (this.resizeCallback) {
      this.resizeCallback();
      this.resizeCallback = null;
    }
    
    // Exit fullscreen if active
    if (this.isFullscreen) {
      this.exitFullscreen().catch(() => {
        // Ignore errors during cleanup
      });
    }
    
    this.app.destroy(true, {
      children: true,
      texture: true,
    });
  }
}

export interface BaseGameConfig {
  showFPSMeter?: boolean;
}

export abstract class BaseGame {
  protected app: GameApp;
  private updateBound: (ticker: Ticker) => void;
  protected fpsMeter: FPSMeter | null = null;
  private showFPSMeter: boolean;

  constructor(app: GameApp, config: BaseGameConfig = {}) {
    this.app = app;
    this.showFPSMeter = config.showFPSMeter ?? true; // Enabled by default
    
    if (this.showFPSMeter) {
      this.fpsMeter = new FPSMeter();
    }
    
    this.updateBound = (ticker: Ticker) => {
      const delta = ticker.deltaMS / 16.67;
      this.update(delta);
      
      // Update FPS meter if enabled
      if (this.fpsMeter) {
        this.fpsMeter.update(delta);
        this.ensureFPSMeterOnTop();
      }
    };
  }

  abstract init(): void;

  abstract update(delta: number): void;

  protected ensureFPSMeterOnTop(): void {
    if (this.fpsMeter) {
      const fpsIndex = this.app.stage.children.indexOf(this.fpsMeter);
      if (fpsIndex !== -1) {
        this.app.stage.setChildIndex(this.fpsMeter, this.app.stage.children.length - 1);
      }
    }
  }

  start(): void {
    // Add FPS meter to stage if enabled
    if (this.fpsMeter && !this.app.stage.children.includes(this.fpsMeter)) {
      this.app.stage.addChild(this.fpsMeter);
      this.ensureFPSMeterOnTop();
    }
    
    this.app.ticker.add(this.updateBound);
  }

  stop(): void {
    this.app.ticker.remove(this.updateBound);
  }

  destroy(): void {
    this.stop();
    
    // Remove FPS meter if it exists
    if (this.fpsMeter && this.app.stage.children.includes(this.fpsMeter)) {
      this.app.stage.removeChild(this.fpsMeter);
      this.fpsMeter.destroy();
      this.fpsMeter = null;
    }
  }
}

export class EventBus {
  private listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();

  on(event: string, callback: (...args: unknown[]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: (...args: unknown[]) => void): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  emit(event: string, ...args: unknown[]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event listener for "${event}":`, error);
        }
      });
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export { FPSMeter } from "./FPSMeter";

